/**
 * EC2 instance-type → max_agents capacity lookup.
 *
 * Single source of truth for "how many agents fit on this host". Read by:
 *  - API provision + resize-commit paths to persist `hosts.max_agents`
 *  - API enforcement paths (`/hosts/:name/assign`, `/agents/create-full`,
 *    `/agents/:codeName/migrate`) via the persisted column
 *  - Webapp host-create / picker UI so the displayed cap matches enforcement
 *
 * Numbers are deliberately conservative — sized for "the manager isn't
 * struggling" rather than "absolute upper bound". Operators who want more
 * can resize to a larger type; auto-resize is intentionally out of scope.
 *
 * Pure functions, no node-only dependencies — safe for browser/edge bundles.
 */

const CAPACITY_TABLE: Record<string, number> = {
  // t3 family — burst-credit instances. Lookup is per-vCPU plus
  // headroom for the manager process itself.
  't3.micro': 1, //   1 vCPU /  1 GB — barely fits one agent
  't3.small': 1, //   2 vCPU /  2 GB — still tight
  't3.medium': 2, //  2 vCPU /  4 GB — the sweet spot for a 2-agent host
  't3.large': 4, //   2 vCPU /  8 GB — bursts cover the headroom
  't3.xlarge': 8, //  4 vCPU / 16 GB
  't3.2xlarge': 16, // 8 vCPU / 32 GB
};

/** Conservative cap for unknown / null instance types. */
const FALLBACK = 1;

/**
 * Resolve the agent-capacity cap for an EC2 instance type.
 *
 * Null / undefined / unknown types → FALLBACK (1). Self-managed hosts
 * with no `ec2_instance_type` set, and any newly-released instance
 * family we haven't catalogued yet, both fall through here. A console
 * warning fires on the unknown-but-non-null branch so prod logs
 * surface the lookup gap before an operator hits the cap and is
 * confused.
 */
export function maxAgentsForInstanceType(
  instanceType: string | null | undefined,
): number {
  if (instanceType == null) return FALLBACK;
  const cap = CAPACITY_TABLE[instanceType];
  if (cap === undefined) {
    console.warn(
      `[ec2-capacity] unknown instance type "${instanceType}" — falling back to ${FALLBACK} agent. ` +
        `Add a row to CAPACITY_TABLE in packages/core/src/provisioning/ec2-capacity.ts.`,
    );
    return FALLBACK;
  }
  return cap;
}

/** Exposed for tests + UI labels — the catalogued types in deterministic order. */
export const KNOWN_INSTANCE_TYPES: ReadonlyArray<string> = Object.keys(CAPACITY_TABLE);

export const FALLBACK_CAPACITY = FALLBACK;
